/**
 * 用途：TikTok 广告洞察页，展示 TikTok 账户维度的广告花费、展示量、点击量等效果指标
 * 路由：/ad-analysis/tt-insights
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { ttInsightsApi, type TtInsightRow, type AccountOption } from '../api/ttInsights';
import useAppStore from '../store/useAppStore';
import { MultiSelect, type SelectOption } from '../components/MultiSelect';
import Pagination from '../components/Pagination';
import { sf, type FieldKey } from '../lib/fieldPolicy';
import { ColumnToggle } from '../components/ColumnToggle';
import { TableLoadingCell, TableLoadingOverlay } from '../components/TableLoadingOverlay';
import { getCurrentCalendarRange, MARKETING_RANGE_PRESETS, MarketingDateRangePicker } from '../components/MarketingTimeRangeFilter';

// ── Metric field definitions ────────────────────────────────────────────────

interface MetricField {
  key: keyof TtInsightRow;
  label: string;
  group: string;
  fmt: (v: number) => string;
  permKey?: FieldKey;
}

const fmtNum = (v: number, d = 2) => v == null ? '-' : v.toLocaleString('en-US', { minimumFractionDigits: d, maximumFractionDigits: d });
const fmtInt = (v: number) => v == null ? '-' : v.toLocaleString('en-US');
const fmtPct = (v: number) => v == null ? '-' : (v * 100).toFixed(2) + '%';

const ALL_METRICS: MetricField[] = [
  // basic (additive)
  { key: 'spend', label: '账户花费', group: '基础指标', fmt: v => fmtNum(v), permKey: 'spend' },
  { key: 'impressions', label: '展示次数', group: '基础指标', fmt: fmtInt },
  { key: 'clicks', label: '点击次数', group: '基础指标', fmt: fmtInt },
  { key: 'reach', label: '触达人数', group: '基础指标', fmt: fmtInt },
  // basic (ratio)
  { key: 'ctr', label: 'CTR', group: '基础指标', fmt: fmtPct },
  { key: 'cpc', label: 'CPC', group: '基础指标', fmt: v => fmtNum(v), permKey: 'cpc' },
  { key: 'cpm', label: 'CPM', group: '基础指标', fmt: v => fmtNum(v), permKey: 'cpm' },
  { key: 'frequency', label: '展示频次', group: '基础指标', fmt: v => fmtNum(v) },
  // conversion
  { key: 'complete_payment_roas', label: 'ROAS', group: '转化指标', fmt: v => fmtNum(v), permKey: 'roas' },
  { key: 'total_purchase', label: '购买次数', group: '转化指标', fmt: v => fmtNum(v) },
  { key: 'total_purchase_value', label: '购买金额', group: '转化指标', fmt: v => fmtNum(v), permKey: 'revenue' },
  { key: 'cost_per_complete_payment', label: 'CPA', group: '转化指标', fmt: v => fmtNum(v), permKey: 'cpa' },
  // shopping
  { key: 'initiate_checkout', label: '发起结账次数', group: '购物指标', fmt: v => fmtNum(v) },
  { key: 'onsite_initiate_checkout_count', label: '站内发起结账次数', group: '购物指标', fmt: v => fmtNum(v) },
  { key: 'total_initiate_checkout_value', label: '发起结账金额', group: '购物指标', fmt: v => fmtNum(v), permKey: 'revenue' },
  // registration
  { key: 'registration', label: '完成注册次数', group: '注册指标', fmt: v => fmtNum(v) },
  { key: 'cost_per_registration', label: '单次注册费用', group: '注册指标', fmt: v => fmtNum(v), permKey: 'cpa' },
  // result
  // engagement
  { key: 'likes', label: '点赞数', group: '互动指标', fmt: fmtInt },
  { key: 'comments', label: '评论数', group: '互动指标', fmt: fmtInt },
  { key: 'shares', label: '分享数', group: '互动指标', fmt: fmtInt },
  { key: 'follows', label: '新增关注数', group: '互动指标', fmt: fmtInt },
  { key: 'profile_visits', label: '主页访问数', group: '互动指标', fmt: fmtInt },
  // video
  { key: 'video_play_actions', label: '视频播放次数', group: '视频指标', fmt: fmtInt },
  { key: 'video_watched_2s', label: '2秒播放数', group: '视频指标', fmt: fmtInt },
  { key: 'video_watched_6s', label: '6秒播放数', group: '视频指标', fmt: fmtInt },
  { key: 'video_views_p25', label: '25%播放数', group: '视频指标', fmt: fmtInt },
  { key: 'video_views_p50', label: '50%播放数', group: '视频指标', fmt: fmtInt },
  { key: 'video_views_p75', label: '75%播放数', group: '视频指标', fmt: fmtInt },
  { key: 'video_views_p100', label: '完播数', group: '视频指标', fmt: fmtInt },
  { key: 'average_video_play', label: '平均播放时长', group: '视频指标', fmt: v => fmtNum(v) },
  { key: 'average_video_play_per_user', label: '人均播放时长', group: '视频指标', fmt: v => fmtNum(v) },
];

const DEFAULT_FIELDS = ['spend', 'impressions', 'clicks', 'ctr', 'cpc', 'cpm', 'total_purchase', 'total_purchase_value', 'complete_payment_roas', 'cost_per_complete_payment'];
// Helper: build SelectOption[] from string[] with a "全部" head and optional "__other__" tail
const mkOpts = (arr: string[], allLabel: string, hasOther = false): SelectOption[] => [
  { value: '', label: allLabel },
  ...arr.map(v => ({ value: v, label: v })),
  ...(hasOther ? [{ value: '__other__', label: '其他' }] : []),
];

const DIMENSION_OPTIONS: SelectOption[] = [
  { value: '', label: '不细分' },
  { value: 'country_code', label: '国家/地区' },
  { value: 'gender', label: '性别' },
  { value: 'age', label: '年龄' },
  { value: 'platform', label: '平台(OS)' },
  { value: 'language', label: '语言' },
  { value: 'interest_category', label: '兴趣分类' },
];
const FUNNEL_LEVEL_OPTS    = mkOpts(['上层', '中层', '下层'], '全部分层');
const CUSTOMER_TYPE_OPTS   = mkOpts(['新客', '老客'], '全部新老客');
const CHANNEL_TIER_OPTS    = mkOpts([
  'tt上层-覆盖人数-7天3次', 'tt上层-覆盖人数-1天1次',
  'tt上层-访问量-落地页', 'tt上层-访问量-应用', 'tt上层-访问量-TTS',
  'tt上层-视频播放量-6svv', 'tt上层-视频播放量-15svv',
  'tt中层-注册', 'tt中层-加购', 'tt下层',
], '全部分层详情');
const AD_FORM_OPTS         = mkOpts(['搜索', 'smart+'], '全部广告形式', true);
const MATERIAL_TYPE_OPTS   = mkOpts(['自建视频', '红人spark-非smart+', '主页spark', '手持视频', '红人spark-smart+', 'smart+cv', '自建视频-cv广告'], '全部素材类型', true);
const OPTIMIZE_TARGET_OPTS = mkOpts(['转化', '价值', 'CPA', 'roas'], '全部优化目标', true);
const CATALOG_TYPE_OPTS    = mkOpts(['所有目录', '优秀目录', '常青款目录'], '全部目录类型', true);
const PRICE_RANGE_OPTS     = mkOpts(['7美金', 'sale', '正价', '原价'], '全部价格带', true);
const TARGETING_TYPE_OPTS  = mkOpts(['有兴趣受众', '无兴趣受众'], '全部定位类型', true);
const AUDIENCE_GROUP_OPTS  = mkOpts(['九大人群', '眼镜包'], '全部受众人群', true);
const CUSTOMER_SEG_OPTS    = mkOpts(['小排', '首单', '排客户列表', '排小排首单老客'], '全部客户类型', true);
const LANDING_PAGE_OPTS    = mkOpts(['116列表', '117列表', '274列表', '455列表', '273列表', '428列表', '专题页', 'SPU搜索页', '详情页'], '全部着陆页', true);

// ── Subtotal / Summary helpers ──────────────────────────────────────────────

const ADDITIVE_KEYS: (keyof TtInsightRow)[] = [
  'spend', 'impressions', 'clicks', 'reach', 'conversion', 'complete_payment',
  'total_purchase', 'total_purchase_value', 'onsite_shopping', 'initiate_checkout',
  'onsite_initiate_checkout_count', 'total_initiate_checkout_value', 'registration',
  'result', 'cta_purchase', 'cta_purchase_value', 'likes', 'comments', 'shares',
  'follows', 'profile_visits', 'video_play_actions', 'video_watched_2s',
  'video_watched_6s', 'video_views_p25', 'video_views_p50', 'video_views_p75',
  'video_views_p100',
];

function computeSubtotal(rows: TtInsightRow[]): TtInsightRow {
  const sub = { stat_time_day: '' } as TtInsightRow;
  for (const key of ADDITIVE_KEYS) {
    (sub as any)[key] = rows.reduce((s, r) => s + ((r[key] as number) || 0), 0);
  }
  sub.ctr = sub.impressions > 0 ? sub.clicks / sub.impressions : 0;
  sub.cpc = sub.clicks > 0 ? sub.spend / sub.clicks : 0;
  sub.cpm = sub.impressions > 0 ? (sub.spend / sub.impressions) * 1000 : 0;
  sub.frequency = sub.reach > 0 ? sub.impressions / sub.reach : 0;
  sub.complete_payment_roas = sub.spend > 0 ? sub.total_purchase_value / sub.spend : 0;
  sub.cost_per_complete_payment = sub.total_purchase > 0 ? sub.spend / sub.total_purchase : 0;
  sub.cost_per_purchase = sub.total_purchase > 0 ? sub.spend / sub.total_purchase : 0;
  sub.value_per_complete_payment = sub.total_purchase > 0 ? sub.total_purchase_value / sub.total_purchase : 0;
  sub.cost_per_onsite_shopping = sub.onsite_shopping > 0 ? sub.spend / sub.onsite_shopping : 0;
  sub.cost_per_registration = sub.registration > 0 ? sub.spend / sub.registration : 0;
  sub.cost_per_result = sub.result > 0 ? sub.spend / sub.result : 0;
  sub.result_rate = sub.clicks > 0 ? sub.result / sub.clicks : 0;
  sub.cta_cost_per_purchase = sub.cta_purchase > 0 ? sub.spend / sub.cta_purchase : 0;
  const totalVPA = sub.video_play_actions;
  sub.average_video_play = totalVPA > 0
    ? rows.reduce((s, r) => s + (r.average_video_play || 0) * (r.video_play_actions || 0), 0) / totalVPA : 0;
  sub.average_video_play_per_user = totalVPA > 0
    ? rows.reduce((s, r) => s + (r.average_video_play_per_user || 0) * (r.video_play_actions || 0), 0) / totalVPA : 0;
  return sub;
}

type DatePreset = 'today' | 'yesterday' | '7d' | '14d' | '30d' | 'thisWeek' | 'thisMonth' | 'custom';

function daysAgo(n: number): string {
  const d = new Date(); d.setDate(d.getDate() - (n - 1));
  return d.toISOString().slice(0, 10);
}
function today(): string { return new Date().toISOString().slice(0, 10); }

// ── Component ───────────────────────────────────────────────────────────────

export default function TtInsights() {
  const { brandId, siteId, adContext } = useAppStore();
  const userLevel = adContext?.data_level ?? 1;
  // filters
  const [datePreset, setDatePreset] = useState<DatePreset>('7d');
  const [customStart, setCustomStart] = useState(() => daysAgo(7));
  const [customEnd, setCustomEnd] = useState(() => today());
  const [selectedAccounts, setSelectedAccounts] = useState<string[]>([]);
  const [dimension, setDimension] = useState('');
  const [adId, setAdId] = useState('');
  const [adName, setAdName] = useState('');
  const [campaignName, setCampaignName] = useState('');
  const [adgroupName, setAdgroupName] = useState('');
  const [funnelLevel, setFunnelLevel] = useState('');
  const [customerType, setCustomerType] = useState('');
  const [channelTier, setChannelTier] = useState('');
  const [adForm, setAdForm] = useState('');
  const [materialType, setMaterialType] = useState('');
  const [optimizeTarget, setOptimizeTarget] = useState('');
  const [catalogType, setCatalogType] = useState('');
  const [priceRange, setPriceRange] = useState('');
  const [targetingType, setTargetingType] = useState('');
  const [audienceGroup, setAudienceGroup] = useState('');
  const [customerSegment, setCustomerSegment] = useState('');
  const [landingPage, setLandingPage] = useState('');
  const [visibleFields, setVisibleFields] = useState<string[]>(DEFAULT_FIELDS);
  const [hiddenMetricKeys, setHiddenMetricKeys] = useState<Set<string>>(() => {
    try {
      const raw = localStorage.getItem('tt_insights_hidden_cols');
      if (raw) return new Set(JSON.parse(raw) as string[]);
    } catch {
      // ignore
    }
    return new Set(ALL_METRICS.map(m => m.key as string).filter(key => !DEFAULT_FIELDS.includes(key)));
  });

  // data
  const [accounts, setAccounts] = useState<AccountOption[]>([]);
  const [data, setData] = useState<TtInsightRow[]>([]);
  const [summary, setSummary] = useState<TtInsightRow | null>(null);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPage, setTotalPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);

  useEffect(() => {
    if (!brandId) return;
    ttInsightsApi.getAccounts({ brand_id: brandId, site_id: siteId || undefined, scope: 'dashboard_read' })
      .then(res => setAccounts(res.data ?? []))
      .catch(() => setAccounts([]));
    setSelectedAccounts([]);
  }, [brandId, siteId]);

  const getDateRange = useCallback(() => {
    if (datePreset === 'thisWeek' || datePreset === 'thisMonth') return getCurrentCalendarRange(datePreset, today());
    if (datePreset === 'today') return { start: today(), end: today() };
    if (datePreset === 'yesterday') return { start: daysAgo(1), end: daysAgo(1) };
    if (datePreset === '7d') return { start: daysAgo(7), end: today() };
    if (datePreset === '14d') return { start: daysAgo(14), end: today() };
    if (datePreset === '30d') return { start: daysAgo(30), end: today() };
    return { start: customStart, end: customEnd };
  }, [datePreset, customStart, customEnd]);

  const fetchData = useCallback(async (p = 1) => {
    const { start, end } = getDateRange();
    setLoading(true);
    try {
      const res = await ttInsightsApi.getList({
        brand_id: brandId,
        site_id: siteId || undefined,
        start_date: start,
        end_date: end,
        advertiser_ids: selectedAccounts.length > 0 ? selectedAccounts.join(',') : undefined,
        dimension: dimension || undefined,
        ad_id: adId || undefined,
        ad_name: adName || undefined,
        campaign_name: campaignName || undefined,
        adgroup_name: adgroupName || undefined,
        funnel_level: funnelLevel || undefined,
        customer_type: customerType || undefined,
        channel_tier: channelTier || undefined,
        ad_form: adForm || undefined,
        material_type: materialType || undefined,
        optimize_target: optimizeTarget || undefined,
        catalog_type: catalogType || undefined,
        price_range: priceRange || undefined,
        targeting_type: targetingType || undefined,
        audience_group: audienceGroup || undefined,
        customer_segment: customerSegment || undefined,
        landing_page: landingPage || undefined,
        page: p,
        size: pageSize,
      });
      const d = res.data;
      setData(d.list ?? []);
      setSummary(d.summary ?? null);
      setTotal(d.total);
      setPage(d.page);
      setTotalPage(d.total_page);
    } catch (e) {
      console.error('Failed to load tt insights:', e);
      setData([]); setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [brandId, siteId, getDateRange, selectedAccounts, dimension, adId, adName, campaignName, adgroupName, funnelLevel, customerType, channelTier, adForm, materialType, optimizeTarget, catalogType, priceRange, targetingType, audienceGroup, customerSegment, landingPage, pageSize]);

  useEffect(() => { fetchData(1); }, [fetchData]);

  const handlePageChange = (p: number) => fetchData(p);

  const activeMetrics = useMemo(() => ALL_METRICS.filter(m => visibleFields.includes(m.key as string)), [visibleFields]);
  const metricToggleCols = useMemo(() => ALL_METRICS.map(m => ({ key: m.key as string, label: m.label })), []);
  const toggleMetricHidden = useCallback((key: string) => {
    setHiddenMetricKeys(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      try { localStorage.setItem('tt_insights_hidden_cols', JSON.stringify([...next])); } catch {}
      return next;
    });
  }, []);
  const resetMetricHidden = useCallback(() => {
    setHiddenMetricKeys(new Set());
    try { localStorage.removeItem('tt_insights_hidden_cols'); } catch {}
  }, []);

  useEffect(() => {
    setVisibleFields(
      ALL_METRICS.map(m => m.key as string).filter(key => !hiddenMetricKeys.has(key)),
    );
  }, [hiddenMetricKeys]);

  const hasAccount = selectedAccounts.length > 0;
  const hasDimension = dimension !== '';

  // Daily subtotals: only when multiple rows per day (account or dimension breakdown)
  const dailySubtotals = useMemo(() => {
    if (!hasAccount && !hasDimension) return new Map<string, TtInsightRow>();
    const groups = new Map<string, TtInsightRow[]>();
    for (const row of data) {
      const date = row.stat_time_day;
      if (!groups.has(date)) groups.set(date, []);
      groups.get(date)!.push(row);
    }
    const subtotals = new Map<string, TtInsightRow>();
    for (const [date, rows] of groups) {
      if (rows.length > 1) subtotals.set(date, computeSubtotal(rows));
    }
    return subtotals;
  }, [data, hasAccount, hasDimension]);

  // ── Row-span: merge date (and account when date+account+dimension) ────────

  const rowSpans = useMemo(() => {
    const dateSpan: number[] = new Array(data.length).fill(1);
    const accountSpan: number[] = new Array(data.length).fill(1);

    const needDateMerge = hasAccount || hasDimension;
    const needAccountMerge = hasAccount && hasDimension;

    if (!needDateMerge) return { dateSpan, accountSpan };

    // merge date
    let i = 0;
    while (i < data.length) {
      let j = i + 1;
      while (j < data.length && data[j].stat_time_day === data[i].stat_time_day) j++;
      dateSpan[i] = j - i;
      for (let k = i + 1; k < j; k++) dateSpan[k] = 0;

      // within same date, merge account
      if (needAccountMerge) {
        let ai = i;
        while (ai < j) {
          let aj = ai + 1;
          while (aj < j && data[aj].advertiser_id === data[ai].advertiser_id) aj++;
          accountSpan[ai] = aj - ai;
          for (let k = ai + 1; k < aj; k++) accountSpan[k] = 0;
          ai = aj;
        }
      }
      i = j;
    }

    return { dateSpan, accountSpan };
  }, [data, hasAccount, hasDimension]);

  const accountOptions: SelectOption[] = useMemo(
    () => accounts.map(a => ({ value: a.account_id, label: a.account_name || a.account_id })),
    [accounts],
  );

  const dimLabel = DIMENSION_OPTIONS.find(d => d.value === dimension)?.label ?? '';

  return (
    <div className="page-section visible">
      {/* ── Date Row ────────────────────────────────────────────────── */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 16, padding: '12px 16px',
        background: 'var(--surface)', borderRadius: 'var(--radius-sm)',
        border: '1px solid var(--border2)', marginBottom: 12,
      }}>
        <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)', whiteSpace: 'nowrap' }}>日期范围</span>
        <div className="page-tabs" style={{ marginBottom: 0 }}>
          {MARKETING_RANGE_PRESETS.map(p => (
            <div key={p.value} className={`page-tab${datePreset === p.value ? ' active' : ''}`} onClick={() => setDatePreset(p.value)}>
              {p.label}
            </div>
          ))}
        </div>
        {datePreset === 'custom' && (
          <MarketingDateRangePicker start={customStart} end={customEnd} max={today()} onChange={({ start, end }) => { setCustomStart(start); setCustomEnd(end); }} />
        )}
      </div>

      {/* ── Filter Grid ─────────────────────────────────────────────── */}
      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: '8px 10px',
        padding: '10px 14px',
        background: 'var(--surface)', borderRadius: 'var(--radius-sm)',
        border: '1px solid var(--border2)', marginBottom: 12,
      }}>
        {[
          { label: '账号',    el: <MultiSelect options={accountOptions} value={selectedAccounts} onChange={setSelectedAccounts} placeholder="全部账号" searchable /> },
          { label: '细分维度', el: <MultiSelect single options={DIMENSION_OPTIONS} value={dimension ? [dimension] : ['']} onChange={v => setDimension(v[0] ?? '')} placeholder="不细分" /> },
          { label: '分层',    el: <MultiSelect single options={FUNNEL_LEVEL_OPTS} value={funnelLevel ? [funnelLevel] : ['']} onChange={v => setFunnelLevel(v[0] === '' ? '' : v[0] ?? '')} placeholder="全部" /> },
          { label: '新老客',  el: <MultiSelect single options={CUSTOMER_TYPE_OPTS} value={customerType ? [customerType] : ['']} onChange={v => setCustomerType(v[0] === '' ? '' : v[0] ?? '')} placeholder="全部" /> },
          { label: '分层详情', el: <MultiSelect single options={CHANNEL_TIER_OPTS} value={channelTier ? [channelTier] : ['']} onChange={v => setChannelTier(v[0] === '' ? '' : v[0] ?? '')} placeholder="全部" /> },
          { label: '广告形式', el: <MultiSelect single options={AD_FORM_OPTS} value={adForm ? [adForm] : ['']} onChange={v => setAdForm(v[0] === '' ? '' : v[0] ?? '')} placeholder="全部" /> },
          { label: '素材类型', el: <MultiSelect single options={MATERIAL_TYPE_OPTS} value={materialType ? [materialType] : ['']} onChange={v => setMaterialType(v[0] === '' ? '' : v[0] ?? '')} placeholder="全部" /> },
          { label: '优化目标', el: <MultiSelect single options={OPTIMIZE_TARGET_OPTS} value={optimizeTarget ? [optimizeTarget] : ['']} onChange={v => setOptimizeTarget(v[0] === '' ? '' : v[0] ?? '')} placeholder="全部" /> },
          { label: '目录类型', el: <MultiSelect single options={CATALOG_TYPE_OPTS} value={catalogType ? [catalogType] : ['']} onChange={v => setCatalogType(v[0] === '' ? '' : v[0] ?? '')} placeholder="全部" /> },
          { label: '价格带',  el: <MultiSelect single options={PRICE_RANGE_OPTS} value={priceRange ? [priceRange] : ['']} onChange={v => setPriceRange(v[0] === '' ? '' : v[0] ?? '')} placeholder="全部" /> },
          { label: '定位类型', el: <MultiSelect single options={TARGETING_TYPE_OPTS} value={targetingType ? [targetingType] : ['']} onChange={v => setTargetingType(v[0] === '' ? '' : v[0] ?? '')} placeholder="全部" /> },
          { label: '受众人群', el: <MultiSelect single options={AUDIENCE_GROUP_OPTS} value={audienceGroup ? [audienceGroup] : ['']} onChange={v => setAudienceGroup(v[0] === '' ? '' : v[0] ?? '')} placeholder="全部" /> },
          { label: '客户类型', el: <MultiSelect single options={CUSTOMER_SEG_OPTS} value={customerSegment ? [customerSegment] : ['']} onChange={v => setCustomerSegment(v[0] === '' ? '' : v[0] ?? '')} placeholder="全部" /> },
          { label: '着陆页',  el: <MultiSelect single options={LANDING_PAGE_OPTS} value={landingPage ? [landingPage] : ['']} onChange={v => setLandingPage(v[0] === '' ? '' : v[0] ?? '')} placeholder="全部" /> },
        ].map(({ label, el }) => (
          <div key={label} style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <label style={{ fontSize: 11, fontWeight: 500, color: 'var(--text2)' }}>{label}</label>
            {el}
          </div>
        ))}
      </div>

      {/* ── Search & Fields Row ──────────────────────────────────────── */}
      <div className="tag-grid" style={{
        display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: '8px 10px',
        padding: '10px 14px', background: 'var(--surface)', borderRadius: 'var(--radius-sm)',
        border: '1px solid var(--border2)', marginBottom: 12,
      }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <label style={{ fontSize: 11, fontWeight: 500, color: 'var(--text2)' }}>广告ID</label>
          <input className="form-input" placeholder="精确匹配" value={adId} onChange={e => setAdId(e.target.value)} />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <label style={{ fontSize: 11, fontWeight: 500, color: 'var(--text2)' }}>广告名称</label>
          <input className="form-input" placeholder="模糊搜索" value={adName} onChange={e => setAdName(e.target.value)} />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <label style={{ fontSize: 11, fontWeight: 500, color: 'var(--text2)' }}>系列名称</label>
          <input className="form-input" placeholder="模糊搜索" value={campaignName} onChange={e => setCampaignName(e.target.value)} />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <label style={{ fontSize: 11, fontWeight: 500, color: 'var(--text2)' }}>组名称</label>
          <input className="form-input" placeholder="模糊搜索" value={adgroupName} onChange={e => setAdgroupName(e.target.value)} />
        </div>
      </div>

      {/* ── Info bar ─────────────────────────────────────────────────── */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, margin: '0 0 8px', fontSize: 12, color: 'var(--text3)', flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <span>共 <b style={{ color: 'var(--text)' }}>{total}</b> 条</span>
          {hasDimension && <span style={{ opacity: .5 }}>|</span>}
          {hasDimension && <span>细分: {dimLabel}</span>}
          {hasAccount && <span style={{ opacity: .5 }}>|</span>}
          {hasAccount && <span>已选 {selectedAccounts.length} 个账号</span>}
        </div>
        <ColumnToggle cols={metricToggleCols} hiddenKeys={hiddenMetricKeys} onToggle={toggleMetricHidden} onReset={resetMetricHidden} />
      </div>

      {/* ── Table ────────────────────────────────────────────────────── */}
      <div className="card" style={{ padding: 0, overflow: 'hidden', position: 'relative' }}>
        <div className="table-wrap tt-table-wrap" style={{ maxHeight: 'calc(100vh - 340px)', overflow: 'auto' }}>
          <table style={{ minWidth: 600 }}>
            <thead style={{ position: 'sticky', top: 0, zIndex: 3 }}>
              <tr>
                <th style={{ position: 'sticky', left: 0, zIndex: 4, background: 'var(--surface2)' }}>日期</th>
                {hasAccount && <th>账号</th>}
                {hasDimension && <th>{dimLabel}</th>}
                {activeMetrics.map(m => <th key={m.key as string} style={{ textAlign: 'right' }}>{m.label}</th>)}
              </tr>
            </thead>
            <tbody>
              {/* -- Summary row -- */}
              {summary && (
                <tr style={{ background: '#fffbeb', fontWeight: 600, fontSize: 12 }}>
                  <td style={{ position: 'sticky', left: 0, zIndex: 1, background: '#fffbeb', color: 'var(--text2)' }}>
                    汇总
                  </td>
                  {hasAccount && <td></td>}
                  {hasDimension && <td></td>}
                  {activeMetrics.map(m => {
                    const raw = m.fmt(summary[m.key] as number);
                    const display = m.permKey ? (sf(raw, m.permKey, userLevel).value ?? '🔒') : raw;
                    return (
                      <td key={m.key as string} style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: 'var(--text)' }}>
                        {display}
                      </td>
                    );
                  })}
                </tr>
              )}
              {loading && data.length === 0 ? (
                <tr><td colSpan={99}><TableLoadingCell /></td></tr>
              ) : data.length === 0 ? (
                <tr><td colSpan={99} style={{ textAlign: 'center', padding: 48, color: 'var(--text3)' }}>暂无数据</td></tr>
              ) : data.map((row, idx) => {
                const isDateGroupEnd = idx === data.length - 1 || data[idx + 1]?.stat_time_day !== row.stat_time_day;
                const subtotal = isDateGroupEnd ? dailySubtotals.get(row.stat_time_day) : undefined;

                return (
                  <React.Fragment key={idx}>
                    <tr>
                      {rowSpans.dateSpan[idx] > 0 && (
                        <td
                          rowSpan={rowSpans.dateSpan[idx]}
                          style={{ position: 'sticky', left: 0, zIndex: 1, background: 'var(--surface)', fontWeight: 500, verticalAlign: 'top' }}
                        >
                          {row.stat_time_day?.slice(0, 10)}
                        </td>
                      )}
                      {hasAccount && (
                        hasDimension
                          ? (rowSpans.accountSpan[idx] > 0 && (
                              <td rowSpan={rowSpans.accountSpan[idx]} style={{ verticalAlign: 'top' }}>
                                {row.account_name || row.advertiser_id}
                              </td>
                            ))
                          : <td>{row.account_name || row.advertiser_id}</td>
                      )}
                      {hasDimension && <td>{row.dimension_value ?? ''}</td>}
                      {activeMetrics.map(m => {
                        const raw = m.fmt(row[m.key] as number);
                        const display = m.permKey ? (sf(raw, m.permKey, userLevel).value ?? '🔒') : raw;
                        return (
                          <td key={m.key as string} style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                            {display}
                          </td>
                        );
                      })}
                    </tr>
                    {subtotal && (
                      <tr style={{ background: 'rgba(18,16,184,.04)', fontWeight: 600, fontSize: 12 }}>
                        <td style={{ position: 'sticky', left: 0, zIndex: 1, background: 'rgba(18,16,184,.04)', color: 'var(--primary)' }}>
                          小计
                        </td>
                        {hasAccount && <td></td>}
                        {hasDimension && <td></td>}
                        {activeMetrics.map(m => (
                          <td key={m.key as string} style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                            {m.fmt(subtotal[m.key] as number)}
                          </td>
                        ))}
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
          {loading && data.length > 0 && <TableLoadingOverlay />}
        </div>
      </div>

      {total > 0 && (
        <Pagination page={page} totalPages={totalPage} onChange={handlePageChange} total={`共 ${total} 条`} pageSize={pageSize} pageSizeOptions={[20, 50, 100]} onPageSizeChange={setPageSize} disabled={loading} style={{ marginTop: 12 }} />
      )}
    </div>
  );
}
